/*
  Linter thinks there are undef classes but that's a lie.
  http://i.imgflip.com/2ecefi.jpg
  And purposely ignore dynamic classes coming from API.
*/
/* eslint-disable css-modules/no-undef-class*/
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { BLANK_IMAGE_PLACEHOLDER } from 'constants/appConstants';
import { evNavigationClick, evTopLevelNavigationClick } from 'events/headerFooter';

import Link from 'components/hf/HFLink';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';
import { stripSpecialCharsDashReplace as strip } from 'helpers';
import css from 'styles/components/hf/zappos/headerNav.scss';

const range = (start, end) => (
  Array.from(Array(end - start + 1).keys()).map(i => i + start)
);

const HeaderSubNav = ({ data, isSubNavOpen, parentText, parentId, isMobile, subCatHeight,
  handleSubNavClick, handleSubNavClose, navsThatHaveBeenOpened, testId }) => {
  const hasBeenOpened = navsThatHaveBeenOpened.includes(parentId);

  if (!data) {
    return null;
  }

  const { componentName, heading, subNavMenu, bgcolor, maintext } = data;

  const makeSubNavMenu = () => {
    const styles = isMobile && parentId !== 'navBrands' && subCatHeight ? { height: subCatHeight } : null;
    const hasSubNav = subNavMenu && !!subNavMenu.length;
    const toggleId = `${parentId}=${heading.text}`;
    return (
      <>
        {heading.text &&
          <Link
            data-hfsubnav={toggleId}
            data-test-id={testId(`${parentText}-${heading.text}`)}
            onClick={e => {
              // mobile subnavs in brands tab are displayed differently =-[
              if (hasSubNav && parentText.toLowerCase() !== 'brands') {
                handleSubNavClick(e);
              }
              trackEvent('TE_HEADERFOOTER_MAIN_NAV', heading.gae || heading.text);
              trackLegacyEvent('Main-Nav', strip(parentText), strip(heading.gae || heading.text));
              track(evNavigationClick, {
                valueClicked: heading.text,
                parentDropdown: parentText
              });
            }}
            aria-expanded={hasSubNav ? isSubNavOpen(toggleId) : null}
            className={cn(css.submenu, { [heading.type]: !!heading.type })}
            to={heading.link}>{heading.text}</Link>
        }
        <ul className={css.subcategory} style={styles}>
          <li data-close><button type="button" onClick={handleSubNavClose} aria-label={`Close ${heading.text} submenu`}/></li>
          {hasSubNav && subNavMenu.map(({ link, text, type, gae }) =>
            text && <li key={link} className={cn(type)}>
              <Link
                to={link}
                data-test-id={testId(`${parentText}-${heading.text || ''}-${text || gae}`)}
                onClick={() => {
                  trackEvent('TE_HEADERFOOTER_MAIN_NAV', gae || text);
                  trackLegacyEvent('Main-Nav', strip(parentText), strip(gae || text));
                  track(evNavigationClick, {
                    valueClicked: text,
                    parentDropdown: parentText
                  });
                }}>
                {text}</Link>
            </li>
          )}
        </ul>
      </>
    );
  };

  const makeClickme = () => {
    const { image, retina, alt, link, maintext: compMainText, gae } = data.clickmes[0];
    const bgColorStyle = { background: bgcolor };

    return (
      <aside className={cn('hfImagesComponent', css.headernavClickmeContainer)}>
        <Link
          to={link}
          className={cn(css.clickme, css.headernavClickMe)}
          style={bgcolor ? bgColorStyle : null}
          data-test-id={testId(`${parentText}-${compMainText}`)}
          onClick={() => {
            trackEvent('TE_HEADERFOOTER_NAV_CLICKME', gae || compMainText);
            trackLegacyEvent('Global-Header-zapposheader', 'Clickmes', strip(gae || compMainText));
            track(evNavigationClick, {
              valueClicked: compMainText,
              parentDropdown: parentText
            });
          }}>
          {image && <img
            src={hasBeenOpened ? image : BLANK_IMAGE_PLACEHOLDER}
            srcSet={hasBeenOpened && retina ? `${image}, ${retina} 2x` : null}
            alt={alt}/>}
          {compMainText && <p className={css.compHeading}>{compMainText}</p>}
        </Link>
      </aside>
    );
  };

  const makeImageGrid = () => (
    <div className={cn(data.type, css.imagesGridComponent)}>
      {/* ^Can't use css[data.type] localized classes above. Works locally but not with prod builds */}
      {maintext && <p className={css.compHeading}>{maintext}</p>}
      <div className={css.imagesContainer}>
        {data.images.map(({ src, href, alt, gae }) =>
          <div key={src}>
            <Link
              to={href}
              data-test-id={testId(`${parentText}-${maintext || ''}-${alt || gae}`)}
              onClick={() => {
                trackEvent('TE_HEADERFOOTER_NAV_IMAGESGRID', gae || alt);
                trackLegacyEvent('Global-Header-zapposheader', 'ImagesGrid', strip(gae || alt));
                track(evNavigationClick, {
                  valueClicked: gae || alt,
                  parentDropdown: parentText
                });
              }}>
              <img alt={alt} src={hasBeenOpened ? src : BLANK_IMAGE_PLACEHOLDER}/>
            </Link>
          </div>
        )}
      </div>
    </div>
  );

  switch (componentName) {
    case 'subNavMenu':
      return makeSubNavMenu();
    case 'clickmes':
      return makeClickme();
    case 'catskillsImageGrid':
    case 'imageGrid':
      return makeImageGrid();
    default:
      return null;
  }
};

const HeaderNav = ({ topLevelNavs, subNavs, isOpen, isSubNavOpen, isMobile,
  mobileStyles, desktopStyles, handleTopNavClick, handleTopNavCloseClick, handleSubNavClick,
  handleSubNavClose, openedSubNav, handleSearchSubmit, navsThatHaveBeenOpened }, { testId }) => {

  if (topLevelNavs) {
    return (
      topLevelNavs.map((nav, index) => {
        const { text, searchurl, link, nochildren } = nav;
        const subNav = subNavs?.[`headermenu${index + 1}`]?.slotData;
        const hasDropDown = !nochildren && subNav && !!Object.keys(subNav).length;
        const parentId = `nav${text}`;

        let catStyles = null;
        if (isMobile) {
          const { marginTop } = mobileStyles;
          catStyles = marginTop ? { marginTop: marginTop } : null;
        } else {
          catStyles = desktopStyles?.categories?.[index];
        }

        const subNavProps = {
          isSubNavOpen,
          isMobile,
          parentText: text,
          parentId,
          handleSubNavClick,
          handleSubNavClose,
          navsThatHaveBeenOpened,
          subCatHeight: mobileStyles.subCatHeight,
          testId
        };

        return (
          <li key={text} className={css[parentId]}>
            {text &&
              <Link
                data-shyguy={parentId}
                data-test-id={testId(text)}
                className={cn(css.topNav, { [css.noArrow]: !hasDropDown })}
                onClick={e => {
                  if (hasDropDown) {
                    handleTopNavClick(e);
                  }
                  trackEvent('TE_HEADERFOOTER_MAIN_NAV', text);
                  track(evTopLevelNavigationClick, {
                    valueClicked: text
                  });
                }}
                aria-expanded={hasDropDown ? isOpen(parentId) : null}
                to={link}>
                {text}
              </Link>
            }
            {hasDropDown &&
              <div
                className={cn(css.category, { [css.locked]: !!openedSubNav })}
                style={catStyles}
                data-test-id={testId(`${parentId}Dropdown`)}
                data-headercategory={parentId}>
                <button
                  type="button"
                  data-close
                  onClick={handleTopNavCloseClick}
                  aria-label={`Close ${text} menu`}/>
                <div className={css.flexNavContainer}>
                  <section>
                    {range(1, 5).map(i =>
                      <HeaderSubNav key={i} {...subNavProps} data={subNav[`submenu-${i}`]}/>
                    )}
                  </section>
                  <section>
                    {range(6, 10).map(i =>
                      <HeaderSubNav key={i} {...subNavProps} data={subNav[`submenu-${i}`]}/>
                    )}
                  </section>
                  <section>
                    {range(11, 15).map(i =>
                      <HeaderSubNav key={i} {...subNavProps} data={subNav[`submenu-${i}`]}/>
                    )}
                  </section>
                  <section>
                    {range(16, 20).map(i =>
                      <HeaderSubNav key={i} {...subNavProps} data={subNav[`submenu-${i}`]}/>
                    )}
                  </section>
                  {searchurl &&
                    <form
                      onSubmit={handleSearchSubmit}
                      className={css.nestedSearchbar}
                      method="GET"
                      action="/search"
                      data-search-id={text}
                      data-test-id={testId(`${parentId}SearchForm`)}
                      data-search-category={searchurl !== 'global' ? searchurl : null}>
                      <label htmlFor={`search${text}`}>{`Search Within ${text}`}</label>
                      <input
                        data-test-id={testId(`${parentId}SearchInput`)}
                        required
                        type="search"
                        name="term"
                        id={`search${text}`}
                        placeholder={`Search Within ${text}`}
                        autoComplete="off"/>
                      <button data-test-id={testId(`${parentId}SearchSubmit`)} type="submit">Search</button>
                    </form>
                  }
                </div>
              </div>
            }
          </li>
        );
      })
    );
  }
  return null;
};

HeaderNav.contextTypes = {
  testId: PropTypes.func
};

export default HeaderNav;
/* eslint-enable*/
